/**
 * Mercado de escolas privadas.
 *
 * A tela responde três perguntas, nesta ordem, porque é a ordem em que a
 * decisão comercial acontece:
 *
 *  1. **Quanto existe?** — receita potencial de toda a rede privada do estado.
 *  2. **Onde está?** — concentração por escola e por município. Se 80 % do
 *     valor cabe em 60 escolas, a operação é uma lista de ligações; se está em
 *     900, é campanha. Uma tabela ordenada não responde isso.
 *  3. **Quem falta procurar?** — o funil, que separa quem já publica de quem
 *     nunca foi contatado.
 *
 * Cada número traz de onde veio. Misturar dado do Censo com estimativa sem
 * dizer qual é qual é como uma planilha de mercado vira uma meta que ninguém
 * bate — e ninguém sabe por quê.
 */

import { ROTULOS_FUNIL, type SituacaoFunil } from '@egba-do/shared'
import { useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { api, type Item, type Lista } from '../api/cliente'
import { BarrasHorizontais, CurvaConcentracao, Distribuicao } from '../componentes/graficos'
import {
  Aviso,
  Botao,
  Campo,
  Carregando,
  Cartao,
  Etiqueta,
  Selecao,
  Tabela,
  Vazio,
  formatarData,
} from '../componentes/ui'

const TOM: Record<SituacaoFunil, 'neutro' | 'marca' | 'sucesso' | 'atencao'> = {
  cliente: 'sucesso',
  em_negociacao: 'marca',
  lead: 'atencao',
  nao_trabalhada: 'neutro',
}

function brl(centavos: number): string {
  return (centavos / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  })
}

const numero = (n: number): string => n.toLocaleString('pt-BR')

interface Resumo {
  ano: number
  valorCmColunaCentavos: number
  metrica: {
    taxaConclusao: number
    fracaoConclusaoTecnico: number
    colunas: number
    alturaLinhaCm: number
    nomesPorLinha: number
  }
  escolasNoUniverso: number
  enriquecidas: number
  resumo: {
    escolas: number
    concluintes: number
    valorCentavos: number
    escolasPara80Pct: number
    porSituacao: Record<SituacaoFunil, { escolas: number; concluintes: number; valorCentavos: number }>
  }
  porMunicipio: Array<{ municipio: string; escolas: number; concluintes: number; valorCentavos: number }>
  curvaConcentracao: Array<{ escolas: number; pctEscolas: number; pctValor: number; valorCentavos: number }>
  cenarios: Array<{ escolas: number; valorCentavos: number; pctValor: number; concluintes: number }>
  realizado: {
    valorCentavos: number
    pedidos: number
    escolas: number
    agendados: number
    valorAgendadoCentavos: number
    itens: Array<{
      id: string
      protocolo: string
      escola: string | null
      valorCentavos: number
      dataPublicacao: string | null
      agendado: boolean
    }>
  }
  tabelaPreco: {
    id: string
    descricao: string
    atoAprovacao: string | null
    vigenciaInicio: string
    valorCmColunaCentavos: number
  } | null
  porFaixa: Array<{ faixa: string; escolas: number; valorCentavos: number }>
  porPorte: Array<{ porte: string; escolas: number; valorCentavos: number }>
  anosDisponiveis: number[]
  procedencia: { ano: number; arquivo: string | null; md5: string | null; concluidoEm: string | null } | null
}

interface EscolaLinha {
  id: string
  coEntidade: number
  noEntidade: string
  noMunicipio: string | null
  razaoSocial: string | null
  porte: string | null
  situacaoCadastral: string | null
  cnpjEscola: string | null
  cnpjMantenedora: string | null
  nuDdd: string | null
  nuTelefone: string | null
  emailReceita: string | null
  telefoneReceita: string | null
  ofereceMedio: boolean
  ofereceTecnico: boolean
  qtMatBasica: number
  qtMatMedio: number
  medio3: number
  medioTec3: number
  medioNormal3: number
  tecConc: number
  tecSubs: number
  ejaMedio: number
  situacao: SituacaoFunil
  matriculasElegiveis: number
  concluintesEstimados: number
  cmColunaEstimado: number
  valorEstimadoCentavos: number
}

/**
 * Abre a conta por trás de um número.
 *
 * Todo indicador desta tela é derivado — de matrícula do Censo, de uma taxa,
 * de uma tabela de preço. Um número derivado que não mostra a derivação é um
 * número que ninguém consegue contestar nem corrigir; e quando ele estiver
 * errado, vai ser defendido em reunião justamente por não ter origem visível.
 */
function ComoCalculado({ children }: { children: ReactNode }) {
  const [aberto, setAberto] = useState(false)
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setAberto((a) => !a)}
        aria-expanded={aberto}
        className="text-[11px] font-medium text-marca-700 underline decoration-dotted underline-offset-2 transition hover:text-marca-900"
      >
        {aberto ? 'ocultar a conta' : 'como este número é calculado'}
      </button>
      {aberto && (
        <div className="mt-2 space-y-1 rounded-lg bg-slate-50 p-3 text-[11px] leading-relaxed text-slate-600">
          {children}
        </div>
      )}
    </div>
  )
}

function Indicador({
  rotulo,
  valor,
  detalhe,
  origem,
  children,
}: {
  rotulo: string
  valor: string
  detalhe?: string
  origem: 'censo' | 'estimativa' | 'nosso'
  children?: ReactNode
}) {
  const fonte = {
    censo: { texto: 'Censo Escolar/INEP', cor: 'text-marca-700 bg-marca-50' },
    estimativa: { texto: 'estimativa', cor: 'text-amber-800 bg-[color:var(--color-atencao-fundo)]' },
    nosso: { texto: 'dado da plataforma', cor: 'text-emerald-800 bg-[color:var(--color-sucesso-fundo)]' },
  }[origem]

  return (
    <div className="rounded-xl border border-[color:var(--color-borda)] bg-white p-4">
      <p className="text-xs font-medium text-slate-500">{rotulo}</p>
      <p className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">{valor}</p>
      {detalhe && <p className="mt-0.5 text-xs text-slate-500">{detalhe}</p>}
      <span className={`mt-2 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${fonte.cor}`}>
        {fonte.texto}
      </span>
      {children && <ComoCalculado>{children}</ComoCalculado>}
    </div>
  )
}

export function Mercado() {
  const [busca, setBusca] = useState('')
  const [municipio, setMunicipio] = useState('')
  const [situacao, setSituacao] = useState('')
  // 150 linhas de uma vez viram dez telas de rolagem para achar a décima
  // escola. Quarenta cabem na leitura, e quem quiser a lista inteira pede.
  const [limite, setLimite] = useState(40)
  // O universo tem 2.954 escolas privadas; só ~540 têm etapa que conclui. Ver
  // as outras é legítimo — é quem pode virar mercado no ano em que abrir médio
  // — mas somá-las no potencial faria o mapa mentir. Por isso é filtro, e o
  // padrão é o recorte que gera receita.
  const [escopo, setEscopo] = useState<'mercado' | 'todas'>('mercado')
  /** Recorte "as N maiores", vindo dos cenários. Null = sem recorte. */
  const [top, setTop] = useState<number | null>(null)

  const { data: r, isLoading } = useQuery({
    queryKey: ['mercado-resumo'],
    queryFn: () => api.get<Item<Resumo>>('/api/v1/mercado/resumo'),
  })

  const { data: municipios } = useQuery({
    queryKey: ['mercado-municipios'],
    queryFn: () => api.get<Lista<{ municipio: string; escolas: number }>>('/api/v1/mercado/municipios'),
  })

  const params = new URLSearchParams({ limite: String(limite) })
  if (busca.trim()) params.set('busca', busca.trim())
  if (municipio) params.set('municipio', municipio)
  if (situacao) params.set('situacao', situacao)
  params.set('somenteComPotencial', escopo === 'mercado' ? 'true' : 'false')
  if (top) params.set('top', String(top))

  const { data: lista } = useQuery({
    queryKey: ['mercado-escolas', busca, municipio, situacao, limite, escopo, top],
    queryFn: () => api.get<Lista<EscolaLinha>>(`/api/v1/mercado/escolas?${params}`),
  })

  if (isLoading || !r) return <Carregando />
  const d = r.dados
  const m = d.resumo

  if (d.escolasNoUniverso === 0) {
    return (
      <Vazio
        titulo="O universo ainda não foi importado"
        descricao="Rode a importação do Censo Escolar para montar o mapa de mercado: pnpm --filter @egba-do/api mercado:importar -- --ano 2024 --uf BA --privadas"
      />
    )
  }

  return (
    <div className="space-y-5">
      {/* ------------------------------------------------------- indicadores */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Indicador
          rotulo="Escolas com etapa concluinte"
          valor={numero(m.escolas)}
          detalhe={`de ${numero(d.escolasNoUniverso)} privadas na Bahia`}
          origem="censo"
        >
          <p>
            Escolas da rede privada da Bahia <strong>em atividade</strong> no Censo Escolar{' '}
            {d.ano}, com pelo menos uma matrícula em etapa que conclui: 3ª série do médio
            (propedêutico, integrado ao técnico ou normal), EJA de nível médio, ou técnico
            concomitante/subsequente.
          </p>
          <p>
            As outras {numero(d.escolasNoUniverso - m.escolas)} são creches e escolas sem ensino
            médio — potencial zero hoje. Use o filtro <em>Quais escolas</em> abaixo para vê-las.
          </p>
        </Indicador>
        <Indicador
          rotulo="Concluintes por ano"
          valor={numero(m.concluintes)}
          detalhe={`${Math.round(d.metrica.taxaConclusao * 100)}% de aprovação no médio`}
          origem="estimativa"
        >
          <p>
            <strong>Matrícula não é conclusão.</strong> Sobre a matrícula publicada pelo Censo:
          </p>
          <p>
            · séries de formatura × {Math.round(d.metrica.taxaConclusao * 100)}% (aprovação da rede
            privada)
            <br />· técnico concomitante e subsequente ×{' '}
            {Math.round(d.metrica.fracaoConclusaoTecnico * 100)}% — a coluna do Censo traz a
            matrícula do curso inteiro, não do ano de formatura
          </p>
          <p>
            As duas taxas são parâmetro em{' '}
            <Link to="/configuracoes" className="text-marca-700 underline">
              Configurações → mercado.composicao
            </Link>
            , calibráveis pelos pedidos já publicados.
          </p>
        </Indicador>
        <Indicador
          rotulo="Se todas publicarem"
          valor={brl(m.valorCentavos)}
          detalhe={`as ${numero(m.escolas)} escolas, a ${brl(d.valorCmColunaCentavos)}/cm-coluna`}
          origem="estimativa"
        >
          <p>
            {numero(m.concluintes)} nomes → centímetros de coluna ({d.metrica.colunas} colunas,
            linha de {d.metrica.alturaLinhaCm} cm) → × preço vigente.
          </p>
          {d.tabelaPreco ? (
            <p>
              Tabela em vigor: <strong>{d.tabelaPreco.descricao}</strong> —{' '}
              {brl(d.tabelaPreco.valorCmColunaCentavos)}/cm-coluna, desde{' '}
              {new Date(`${d.tabelaPreco.vigenciaInicio}T12:00:00`).toLocaleDateString('pt-BR')}.
              {d.tabelaPreco.atoAprovacao ? ` ${d.tabelaPreco.atoAprovacao}.` : ''}{' '}
              <Link to="/precos" className="text-marca-700 underline">
                ver tabelas de preço
              </Link>
            </p>
          ) : (
            <p>
              <strong>Nenhuma tabela de preço vigente cadastrada</strong> — por isso o mercado
              aparece zerado.{' '}
              <Link to="/precos" className="text-marca-700 underline">
                cadastrar
              </Link>
            </p>
          )}
          <p>
            É teto, não previsão: supõe que todas publiquem uma vez no ano. O que já entrou está
            logo abaixo, com a lista de publicações.
          </p>
        </Indicador>
        <Indicador
          rotulo="Concentração"
          valor={`${numero(m.escolasPara80Pct)} escolas`}
          detalhe={`fazem 80% do valor — ${Math.round((m.escolasPara80Pct / Math.max(1, m.escolas)) * 100)}% da rede`}
          origem="estimativa"
        >
          <p>
            Escolas ordenadas da maior para a menor; somando o potencial de cima para baixo,{' '}
            {numero(m.escolasPara80Pct)} bastam para chegar a 80% do total. É o número que decide
            se a operação é uma lista de ligações ou uma campanha.
          </p>
          <p>A curva logo abaixo mostra isso, e cada cenário abre a lista.</p>
        </Indicador>
      </div>

      <Aviso tom="info" titulo="O que é dado e o que é estimativa">
        Matrículas por etapa vêm do <strong>Censo Escolar {d.ano}</strong> e são dado publicado.
        Concluintes e receita são <strong>estimativa</strong>: matrícula na série de formatura ×
        taxa de aprovação, convertida em centímetros de coluna pela métrica de composição e
        multiplicada pela tabela vigente. Faturamento da escola não é dado público e não entra em
        nenhuma conta aqui; índice acadêmico da rede privada também não existe de forma
        censitária — o IDEB depende do SAEB, cuja adesão privada é baixa.
      </Aviso>

      {/* -------------------------------------------------------------- funil */}
      <Cartao
        titulo="Funil"
        descricao="Onde cada escola do mercado está na relação com a EGBA. Clique para filtrar a lista."
      >
        <Aviso tom="info">
          <strong>Lead é quem agiu.</strong> Conta como lead a escola que preencheu formulário de
          landing page ou recebeu envio de campanha. Estar na base de CNPJ importada da Receita{' '}
          <em>não</em> é lead — é universo, e universo aparece como “não trabalhada”, que é a
          verdade sobre ela.
        </Aviso>
        <div className="h-3" />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {(Object.keys(ROTULOS_FUNIL) as SituacaoFunil[]).map((s) => {
            const v = m.porSituacao[s]
            const pct = m.valorCentavos > 0 ? (v.valorCentavos / m.valorCentavos) * 100 : 0
            return (
              <button
                key={s}
                type="button"
                onClick={() => setSituacao(situacao === s ? '' : s)}
                className={`rounded-xl border p-4 text-left transition ${
                  situacao === s
                    ? 'border-marca-400 bg-marca-50'
                    : 'border-[color:var(--color-borda)] bg-white hover:border-marca-300'
                }`}
              >
                <Etiqueta tom={TOM[s]}>{ROTULOS_FUNIL[s].nome}</Etiqueta>
                <p className="mt-2 text-xl font-semibold text-slate-900">{numero(v.escolas)}</p>
                <p className="text-xs text-slate-500">{brl(v.valorCentavos)} · {Math.round(pct)}%</p>
                <p className="mt-1 text-[11px] leading-snug text-slate-400">
                  {ROTULOS_FUNIL[s].descricao}
                </p>
              </button>
            )
          })}
        </div>
      </Cartao>

      {/* --------------------------------------------- do potencial ao caixa */}
      <Cartao
        titulo="Do potencial ao realizado"
        descricao="Quanto do teto já virou publicação de verdade — com a lista de onde veio cada real."
      >
        {(() => {
          const feito = d.realizado.valorCentavos
          const cobertura = m.valorCentavos > 0 ? (feito / m.valorCentavos) * 100 : 0
          return (
            <>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <p className="text-xs text-slate-500">Publicado e no caixa</p>
                  <p className="mt-0.5 text-2xl font-semibold text-emerald-700">{brl(feito)}</p>
                  <p className="text-xs text-slate-500">
                    {d.realizado.pedidos === 0
                      ? 'nenhuma publicação concluída ainda'
                      : `${numero(d.realizado.pedidos)} publicação(ões) · ${numero(d.realizado.escolas)} escola(s)`}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Falta capturar</p>
                  <p className="mt-0.5 text-2xl font-semibold text-slate-900">
                    {brl(Math.max(0, m.valorCentavos - feito))}
                  </p>
                  <p className="text-xs text-slate-500">
                    {numero(Math.max(0, m.escolas - d.realizado.escolas))} escolas ainda sem publicar
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Cobertura do mercado</p>
                  <p className="mt-0.5 text-2xl font-semibold text-slate-900">
                    {cobertura === 0
                      ? '0%'
                      : cobertura < 0.1
                        ? '<0,1%'
                        : `${cobertura.toFixed(1).replace('.', ',')}%`}
                  </p>
                  <p className="text-xs text-slate-500">do teto de {brl(m.valorCentavos)}</p>
                </div>
              </div>

              <div className="mt-4 h-3 overflow-hidden rounded-full bg-slate-100">
                <span
                  className="block h-full rounded-full bg-emerald-600 transition-[width] duration-500"
                  style={{ width: `${Math.max(cobertura > 0 ? 0.6 : 0, cobertura)}%` }}
                />
              </div>

              {/* A lista que sustenta o número. Sem ela, "R$ 14.586 realizados"
                  não pode ser conferido — e foi assim que um pedido de TESTE,
                  com data futura, apareceu como faturamento da EGBA. */}
              <div className="mt-5">
                <p className="mb-2 text-sm font-medium text-slate-800">
                  Publicações que compõem este número
                </p>
                {d.realizado.itens.length === 0 ? (
                  <Aviso tom="info">
                    Nenhum pedido de concluintes publicado até hoje. O valor acima é zero por isso —
                    não é falta de dado, é o estado real da operação.
                  </Aviso>
                ) : (
                  <>
                    <Tabela>
                      <thead>
                        <tr className="border-b border-[color:var(--color-borda-suave)] text-left text-xs tracking-wider text-slate-400 uppercase">
                          <th className="px-3 py-2 font-semibold">Protocolo</th>
                          <th className="px-3 py-2 font-semibold">Escola</th>
                          <th className="px-3 py-2 font-semibold">Publicação</th>
                          <th className="px-3 py-2 text-right font-semibold">Valor</th>
                          <th className="px-3 py-2 font-semibold">Entra na conta?</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {d.realizado.itens.map((i) => (
                          <tr key={i.id}>
                            <td className="px-3 py-2 font-mono text-xs text-slate-700">
                              {i.protocolo}
                            </td>
                            <td className="px-3 py-2 text-sm text-slate-800">{i.escola ?? '—'}</td>
                            <td className="px-3 py-2 text-sm text-slate-600">
                              {i.dataPublicacao
                                ? new Date(`${i.dataPublicacao}T12:00:00`).toLocaleDateString('pt-BR')
                                : 'sem data'}
                            </td>
                            <td className="px-3 py-2 text-right text-sm tabular-nums">
                              {brl(i.valorCentavos)}
                            </td>
                            <td className="px-3 py-2">
                              {i.agendado ? (
                                <Etiqueta tom="atencao">agendado — não conta</Etiqueta>
                              ) : (
                                <Etiqueta tom="sucesso">sim</Etiqueta>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Tabela>
                    {d.realizado.agendados > 0 && (
                      <p className="mt-2 text-xs text-slate-500">
                        {numero(d.realizado.agendados)} pedido(s) com data de publicação futura,
                        somando {brl(d.realizado.valorAgendadoCentavos)}, ficam de fora: marcado
                        como publicado com data à frente é <strong>agendado</strong>, e contá-lo
                        como receita antecipa dinheiro que ainda não entrou.{' '}
                        <Link to="/pedidos" className="text-marca-700 underline">
                          ver em Pedidos
                        </Link>
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* ------------------------------------------------- cenários */}
              <div className="mt-6">
                <p className="mb-1 text-sm font-medium text-slate-800">
                  Quanto vale ir atrás das maiores
                </p>
                <p className="mb-2 text-xs text-slate-500">
                  Cada linha abre a lista exata daquelas escolas, com telefone e e-mail na ficha.
                </p>
                <Tabela>
                  <thead>
                    <tr className="border-b border-[color:var(--color-borda-suave)] text-left text-xs tracking-wider text-slate-400 uppercase">
                      <th className="px-3 py-2 font-semibold">Se conquistar</th>
                      <th className="px-3 py-2 text-right font-semibold">Concluintes</th>
                      <th className="px-3 py-2 text-right font-semibold">Receita</th>
                      <th className="px-3 py-2 text-right font-semibold">Do teto</th>
                      <th className="px-3 py-2 font-semibold">Participação</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {d.cenarios.map((c) => (
                      <tr key={c.escolas} className={top === c.escolas ? 'bg-marca-50' : ''}>
                        <td className="px-3 py-2 text-sm text-slate-800">
                          {c.escolas === m.escolas
                            ? `todas as ${numero(c.escolas)}`
                            : `as ${numero(c.escolas)} maiores`}
                          {c.escolas === m.escolasPara80Pct && (
                            <span className="ml-2 text-xs text-amber-700">← 80% do valor</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-sm text-slate-600 tabular-nums">
                          {numero(c.concluintes)}
                        </td>
                        <td className="px-3 py-2 text-right text-sm font-medium text-slate-900 tabular-nums">
                          {brl(c.valorCentavos)}
                        </td>
                        <td className="px-3 py-2 text-right text-sm text-slate-500 tabular-nums">
                          {Math.round(c.pctValor)}%
                        </td>
                        <td className="px-3 py-2">
                          <span className="block h-2 w-full overflow-hidden rounded-full bg-slate-100">
                            <span
                              className="block h-full rounded-full bg-marca-600"
                              style={{ width: `${c.pctValor}%` }}
                            />
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => {
                              setTop(top === c.escolas ? null : c.escolas)
                              setLimite(Math.min(c.escolas, 200))
                              document
                                .getElementById('lista-escolas')
                                ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                            }}
                            className="rounded-lg border border-[color:var(--color-borda)] px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:border-marca-400 hover:text-marca-700"
                          >
                            {top === c.escolas ? 'limpar' : 'ver a lista'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Tabela>
              </div>
            </>
          )
        })()}
      </Cartao>

      {/* ------------------------------------------------------------ gráficos */}
      <div className="grid gap-4 xl:grid-cols-[3fr_2fr]">
        <Cartao
          titulo="Concentração do mercado"
          descricao="Quanto do valor está nas maiores escolas. Quanto mais a curva descola da diagonal, mais concentrado."
        >
          <CurvaConcentracao
            pontos={d.curvaConcentracao}
            marcador={{
              pctEscolas: (m.escolasPara80Pct / Math.max(1, m.escolas)) * 100,
              rotulo: `${numero(m.escolasPara80Pct)} escolas = 80%`,
            }}
          />
        </Cartao>

        <Cartao titulo="Tamanho das escolas" descricao="Escolas por faixa de potencial anual.">
          <Distribuicao
            itens={d.porFaixa.map((f) => ({
              rotulo: f.faixa,
              valor: f.escolas,
              dica: `${numero(f.escolas)} escolas · ${brl(f.valorCentavos)} no total`,
            }))}
          />
        </Cartao>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Cartao titulo="Onde está o mercado" descricao="Municípios por receita potencial.">
          <BarrasHorizontais
            itens={d.porMunicipio.slice(0, 12).map((mu) => ({
              rotulo: mu.municipio,
              valor: mu.valorCentavos,
              formatado: brl(mu.valorCentavos),
              detalhe: `${mu.escolas} esc.`,
            }))}
          />
        </Cartao>

        <Cartao titulo="Porte da empresa" descricao="Como a Receita classifica a mantenedora.">
          <BarrasHorizontais
            larguraRotulo={130}
            itens={d.porPorte.map((pt) => ({
              rotulo: pt.porte,
              valor: pt.valorCentavos,
              formatado: brl(pt.valorCentavos),
              detalhe: `${pt.escolas} esc.`,
            }))}
          />
          <p className="mt-3 text-[11px] leading-snug text-slate-400">
            Porte é faixa de receita declarada à Receita para fins tributários (ME até R$ 360 mil,
            EPP até R$ 4,8 milhões). Não é o faturamento da escola e não entra em conta nenhuma —
            está aqui porque diz com que tipo de empresa se vai falar.
          </p>
        </Cartao>
      </div>

      {/* ----------------------------------------------------------- escolas */}
      <div id="lista-escolas" />
      <Cartao
        titulo={top ? `As ${numero(top)} maiores` : 'Escolas'}
        descricao="Ordenadas por receita potencial. Clique para ver a ficha completa."
        acoes={
          top ? (
            <Botao variante="secundario" tamanho="sm" onClick={() => setTop(null)}>
              Ver todas
            </Botao>
          ) : undefined
        }
      >
        <div className="mb-4 grid gap-3 sm:grid-cols-[1fr_200px_180px_190px]">
          <Campo
            rotulo="Buscar"
            placeholder="Nome da escola, razão social ou município"
            value={busca}
            onChange={(e) => { setBusca(e.target.value); setLimite(40) }}
          />
          <Selecao
            rotulo="Município"
            value={municipio}
            onChange={(e) => setMunicipio(e.target.value)}
            opcoes={[
              { valor: '', texto: 'Todos' },
              ...(municipios?.dados ?? []).map((mu) => ({
                valor: mu.municipio,
                texto: `${mu.municipio} (${mu.escolas})`,
              })),
            ]}
          />
          <Selecao
            rotulo="Situação"
            value={situacao}
            onChange={(e) => setSituacao(e.target.value)}
            opcoes={[
              { valor: '', texto: 'Todas' },
              ...(Object.keys(ROTULOS_FUNIL) as SituacaoFunil[]).map((s) => ({
                valor: s,
                texto: ROTULOS_FUNIL[s].nome,
              })),
            ]}
          />
          <Selecao
            rotulo="Quais escolas"
            value={escopo}
            onChange={(e) => {
              setEscopo(e.target.value as 'mercado' | 'todas')
              setLimite(40)
            }}
            opcoes={[
              { valor: 'mercado', texto: 'Com etapa concluinte' },
              { valor: 'todas', texto: `Todas as privadas (${numero(d.escolasNoUniverso)})` },
            ]}
            ajuda={
              escopo === 'todas'
                ? 'Inclui creches e escolas sem ensino médio — potencial zero hoje.'
                : undefined
            }
          />
        </div>

        {(lista?.dados.length ?? 0) === 0 ? (
          <Vazio titulo="Nenhuma escola encontrada" descricao="Ajuste os filtros acima." />
        ) : (
          <>
            <Tabela>
              <thead>
                <tr className="border-b border-[color:var(--color-borda-suave)] text-left text-xs tracking-wider text-slate-400 uppercase">
                  <th className="px-3 py-2 font-semibold">Escola</th>
                  <th className="px-3 py-2 font-semibold">Situação</th>
                  <th className="px-3 py-2 text-right font-semibold">Concluintes</th>
                  <th className="px-3 py-2 text-right font-semibold">Potencial/ano</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {lista!.dados.map((e) => (
                  <tr key={e.id}>
                    <td className="px-3 py-2.5">
                      <p className="font-medium text-slate-900">{e.noEntidade}</p>
                      <p className="text-xs text-slate-500">
                        {e.noMunicipio}
                        {e.porte ? ` · ${e.porte}` : ''}
                        {e.ofereceTecnico ? ' · técnico' : ''}
                      </p>
                    </td>
                    <td className="px-3 py-2.5">
                      <Etiqueta tom={TOM[e.situacao]}>{ROTULOS_FUNIL[e.situacao].nome}</Etiqueta>
                    </td>
                    <td className="px-3 py-2.5 text-right text-sm text-slate-700">
                      {numero(e.concluintesEstimados)}
                    </td>
                    <td className="px-3 py-2.5 text-right text-sm font-medium text-slate-900">
                      {brl(e.valorEstimadoCentavos)}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <Link
                        to={`/mercado/escola/${e.id}`}
                        className="inline-flex items-center rounded-lg border border-[color:var(--color-borda)] px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:border-marca-400 hover:text-marca-700"
                      >
                        Abrir ficha
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tabela>
            <div className="mt-4 flex items-center justify-between gap-4">
              <p className="text-xs text-slate-400">
                {numero(lista!.total)} escolas no filtro · exibindo as {lista!.dados.length} de
                maior potencial.
              </p>
              {lista!.dados.length < lista!.total && (
                <Botao
                  variante="secundario"
                  tamanho="sm"
                  onClick={() => setLimite((l) => l + 80)}
                >
                  Ver mais {Math.min(80, lista!.total - lista!.dados.length)}
                </Botao>
              )}
            </div>
          </>
        )}
      </Cartao>

      {d.procedencia && (
        <p className="text-center text-xs text-slate-400">
          Censo Escolar {d.procedencia.ano} · {d.procedencia.arquivo} · MD5 conferido contra o
          publicado pelo INEP · importado em{' '}
          {d.procedencia.concluidoEm ? formatarData(d.procedencia.concluidoEm) : '—'} ·{' '}
          {numero(d.enriquecidas)} escolas casadas com a base de CNPJ da Receita
        </p>
      )}

    </div>
  )
}
